import type { LeagueJoinPolicy } from '../../types'
import './JoinPolicyChoice.css'

/**
 * "Who can join" — the one control behind both halves: whether a league is listed
 * in Explore, and what happens when someone taps it there.
 *
 * Shared by the create screen and the owner's settings menu so the three options can never drift
 * apart in wording. The explainers say what each choice does to the *listing*, because that is the
 * consequence an owner cannot see from inside their own league.
 */

const JOIN_POLICY_OPTIONS: {
  value: LeagueJoinPolicy
  label: string
  hint: string
}[] = [
  {
    value: 'open',
    label: 'Anyone can join',
    hint: 'Your league is listed in Explore. No approval needed.',
  },
  {
    value: 'request',
    label: 'Ask to join',
    hint: 'Listed in Explore. You approve each request.',
  },
  {
    value: 'invite',
    label: 'Invite only',
    hint: 'Hidden from Explore. Only your invite code gets people in.',
  },
]

interface JoinPolicyChoiceProps {
  value: LeagueJoinPolicy
  onChange: (value: LeagueJoinPolicy) => void
  /** Distinguishes the radio group when two of these are ever on screen at once. */
  name: string
}

function JoinPolicyChoice({ value, onChange, name }: JoinPolicyChoiceProps) {
  return (
    <div className="join-policy" role="radiogroup" aria-label="Who can join">
      {JOIN_POLICY_OPTIONS.map((option) => (
        <label className="join-policy__option" key={option.value}>
          <input
            type="radio"
            className="join-policy__radio"
            name={name}
            value={option.value}
            checked={value === option.value}
            onChange={() => onChange(option.value)}
          />
          <span className="join-policy__text">
            <span className="join-policy__label">{option.label}</span>
            <span className="join-policy__hint">{option.hint}</span>
          </span>
        </label>
      ))}
    </div>
  )
}

export default JoinPolicyChoice
